import Link from "next/link";
import { createContext, useEffect, useRef, useState } from "react";
import { useWidgetSelector, useWidgetStore } from "../store/store";
import { withStaticProps } from "../../../../../util/withStaticProps";
import { getWidgetStaticProps } from "../widgetStaticProps";
import { fetchCauseAreas, fetchReferralTypes } from "../store/flows";
import { setWidgetApiLocale, widgetAsksForReferrals } from "../store/widgetApi";
import { setOperationsConfig, setCauseAreaSelection, setOperationsPercentageModeByCauseArea } from "../store/actions";
import { State } from "../store/state";
import { Carousel } from "./Carousel";
import { SelectionPane } from "./panes/SelectionPane";
import { AmountPane } from "./panes/AmountPane/index";
import { SingleCauseAreaPane } from "./panes/SingleCauseAreaPane";
import { DonorPane } from "./panes/DonorPane/DonorPane";
import { PaymentPane } from "./panes/PaymentPane/PaymentPane";
import { ProgressBar } from "./shared/ProgressBar/ProgressBar";
import { ApiErrorNotification } from "./shared/ApiErrorNotification/ApiErrorNotification";
import { useAvailablePaymentMethods, useAvailableRecurringOptions, useDefaultPaymentMethodEffect, usePrefilledDistribution, usePrefilledSum, usePrefilledCauseAreaIds, useQueryParamsPrefill, useWidgetScaleEffect, WIDGET_CONTENT_WIDTH, WIDGET_FRAME_WIDTH } from "./hooks";
import { useElementHeight } from "../../../../../hooks/useElementHeight";
import { applyWidgetDefaults, DEFAULT_OPERATIONS_CONFIG } from "../utils/widgetDefaults";
import progressBar from "./shared/ProgressBar/ProgressBar.module.scss";
export { widgetContentQuery, widgetQuery } from "../widgetStaticProps";
export const WidgetTooltipContext = createContext<[{
  text: string;
  link?: string;
} | null, any]>([null, () => {}]);
export const Widget = withStaticProps(getWidgetStaticProps)(({
  data,
  inline = false,
  prefilled,
  defaultPaymentType
}) => {
  const widget = applyWidgetDefaults(data.result);
  const methods = widget.methods;
  if (!methods) {
    throw new Error("No payment methods found");
  }
  const store = useWidgetStore();
  const {
    dispatch
  } = store;
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetWrapperRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    text: string;
    link?: string;
  } | null>(null);
  const causeAreas = useWidgetSelector((state: State) => state.layout.causeAreas);
  const prefilledCauseAreaIds = usePrefilledCauseAreaIds();
  const availableRecurringOptions = useAvailableRecurringOptions(methods);
  const availablePaymentMethods = useAvailablePaymentMethods(methods);

  // Platforms with a single (active) cause area keep the pre-rewrite widget UX:
  // no cause-area selection step and no operations/tip. See SingleCauseAreaPane.
  const visibleCauseAreas = causeAreas?.filter(ca => ca.isActive || prefilledCauseAreaIds.has(ca.id)) ?? [];
  const isSingleCauseArea = !!causeAreas && visibleCauseAreas.length === 1;
  const singleCauseAreaId = isSingleCauseArea ? visibleCauseAreas[0].id : undefined;
  useEffect(() => {
    if (singleCauseAreaId === undefined) return;
    // There is nothing to select, so pin the selection to the one cause area and
    // make sure operations/tip stays off (it may be auto-enabled by config).
    dispatch(setCauseAreaSelection("single", singleCauseAreaId));
    dispatch(setOperationsPercentageModeByCauseArea(singleCauseAreaId, false));
  }, [dispatch, singleCauseAreaId]);
  const {
    scaledHeight,
    scalingFactor
  } = useWidgetScaleEffect(widgetRef, inline);
  // const { scrollPosition } = useWidgetScrollObserver(widgetRef);
  const widgetHeight = useElementHeight(widgetRef);
  useEffect(() => {
    // Set operations config first so it's available when cause areas are loaded
    dispatch(setOperationsConfig({
      operationsCauseAreaId: widget.operations_config?.operations_cause_area_id ?? DEFAULT_OPERATIONS_CONFIG.operations_cause_area_id,
      defaultPercentage: widget.operations_config?.default_percentage ?? DEFAULT_OPERATIONS_CONFIG.default_percentage,
      enabledByDefaultGlobal: widget.operations_config?.enabled_by_default_global ?? DEFAULT_OPERATIONS_CONFIG.enabled_by_default_global,
      enabledByDefaultSingle: widget.operations_config?.enabled_by_default_single ?? DEFAULT_OPERATIONS_CONFIG.enabled_by_default_single,
      excludedCauseAreaIds: widget.operations_config?.excluded_cause_area_ids ?? DEFAULT_OPERATIONS_CONFIG.excluded_cause_area_ids
    }));
    // The Danish site's backend is REST only (see store/widgetApi.ts)
    setWidgetApiLocale(widget.locale);
    void fetchCauseAreas(store);
    if (widgetAsksForReferrals()) void fetchReferralTypes(store);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- initialises the store once per widget locale
  }, [store, widget.locale]);
  usePrefilledDistribution({
    inline,
    causeAreas,
    prefilledDistribution: prefilled
  });
  useQueryParamsPrefill({
    inline,
    causeAreas,
    defaultPaymentType
  });

  // After useQueryParamsPrefill, so a monthly choice the widget was opened with isn't replaced
  // by defaultPaymentType when both apply in the same render
  usePrefilledSum({
    inline,
    causeAreas
  });
  useDefaultPaymentMethodEffect(methods);
  let tooltipReadmoreText: string;
  switch (widget.locale) {
    case "no":
      tooltipReadmoreText = "Les mer";
      break;
    case "sv":
      tooltipReadmoreText = "Läs mer";
      break;
    default:
      tooltipReadmoreText = "Read more";
  }
  return <div className="widget-wrapper" ref={widgetWrapperRef} style={{
    height: inline ? `${widgetHeight * scalingFactor}px` : "auto",
    width: scalingFactor * (inline ? WIDGET_CONTENT_WIDTH : WIDGET_FRAME_WIDTH)
  }}>
      <div className={`widget${inline ? " widget--inline" : ""}`} ref={widgetRef} style={{
      transform: `scale(${scalingFactor})`,
      height: inline ? "auto" : `${scaledHeight}px`,
      flexBasis: inline ? "auto" : `${scaledHeight}px`,
      transformOrigin: inline ? "top left" : undefined
    }}>
        <WidgetTooltipContext.Provider value={[tooltip, setTooltip]}>
          {tooltip !== null && <div className={progressBar.tooltipWrapper}>
              <div className={progressBar.tooltipContent}>{tooltip.text}</div>
              {tooltip.link && <Link className={progressBar.tooltipLink} href={tooltip.link} target="_blank">
                  {tooltipReadmoreText} ↗
                </Link>}
            </div>}
          <ApiErrorNotification genericErrorMessage={widget.api_generic_error_message} />
          {/*(() => {
            const taxInfoText: Record<string, string> = {
              no: "For skattefradrag i 2025 må pengene stå på vår konto innen årsslutt. Bankdonasjoner må overføres før kl. 14:30 den 31. desember.",
              sv: "För skatteavdrag 2025 måste pengarna finnas på vårt konto innan årsskiftet. Banköverföringar måste skickas före kl. 14:30 den 31 december.",
            };
            const text = taxInfoText[widget.locale];
            return text ? <TaxInfoBox>{text}</TaxInfoBox> : null;
           })()}*/}
          <ProgressBar inline={inline} numberOfPanes={isSingleCauseArea ? 3 : 4} />
          <Carousel minHeight={inline ? 0 : scaledHeight - 116}>
            {isSingleCauseArea ? <SingleCauseAreaPane nextButtonText={widget.pane1_button_text} enableRecurring={availableRecurringOptions.recurring} enableSingle={availableRecurringOptions.single} singleDonationText={widget.single_donation_text} monthlyDonationText={widget.monthly_donation_text} amountContext={widget.amount_context} smartDistributionContext={widget.smart_distribution_context} /> : false}
            {!isSingleCauseArea ? <SelectionPane causeAreaDisplayConfig={widget.cause_area_display_config} accentColor={widget.accentColor} /> : false}
            {!isSingleCauseArea ? <AmountPane nextButtonText={widget.pane1_button_text} smartDistContext={widget.smart_distribution_context} text={{
            single_donation_text: widget.single_donation_text,
            monthly_donation_text: widget.monthly_donation_text
          }} enableRecurring={availableRecurringOptions.recurring} enableSingle={availableRecurringOptions.single} amountContext={widget.amount_context} operationsConfig={widget.operations_config} causeAreaDisplayConfig={widget.cause_area_display_config} uiLabels={widget.ui_labels} /> : false}
            <DonorPane locale={widget.locale} text={{
            anon_button_text: widget.anon_button_text,
            anon_button_text_tooltip: widget.anon_button_text_tooltip,
            name_placeholder: widget.name_placeholder,
            name_invalid_error_text: widget.name_invalid_error_text,
            email_placeholder: widget.email_placeholder,
            email_invalid_error_text: widget.email_invalid_error_text,
            tax_deduction_selector_text: widget.tax_deduction_selector_text,
            tax_deduction_ssn_placeholder: widget.tax_deduction_ssn_placeholder,
            tax_deduction_ssn_invalid_error_text: widget.tax_deduction_ssn_invalid_error_text,
            tax_deduction_tooltip_text: widget.tax_deduction_tooltip_text,
            tax_deduction_ssn_suspicious_message: widget.tax_deduction_ssn_suspicious_message,
            newsletter_selector_text: widget.newsletter_selector_text,
            privacy_policy_text: widget.privacy_policy_text,
            privacy_policy_link: widget.privacy_policy_link,
            pane2_button_text: widget.pane2_button_text,
            api_generic_error_message: widget.api_generic_error_message,
            show_name_field: widget.show_name_field,
            allow_anonymous_donations: widget.allow_anonymous_donations,
            require_privacy_policy_checkbox: widget.require_privacy_policy_checkbox,
            privacy_policy_required_error_text: widget.privacy_policy_required_error_text,
            show_referral_code_field: widget.show_referral_code_field,
            referral_code_placeholder: widget.referral_code_placeholder
          }} summaryText={{
            single_donation_text: widget.single_donation_text,
            monthly_donation_text: widget.monthly_donation_text,
            smart_distribution_title: widget.smart_distribution_context.smart_distribution_title,
            operations_summary_label: widget.ui_labels?.operations_summary_label ?? "",
            total_label: widget.ui_labels?.total_label ?? ""
          }} paymentMethods={availablePaymentMethods} isSingleCauseArea={isSingleCauseArea} />
            <PaymentPane referrals={{
            referrals_title: widget.referrals_title,
            other_referral_input_placeholder: widget.other_referral_input_placeholder,
            show_referrals: widget.show_referrals
          }} paymentMethods={availablePaymentMethods} />
          </Carousel>
        </WidgetTooltipContext.Provider>
      </div>
    </div>;
});